import {
  forwardRef,
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { IoCloseOutline, IoSearchOutline } from 'react-icons/io5';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

type SavedLocation = {
  id: number;
  name: string;
  path: string;
  section_id?: number;
  section?: string;
  rack_id?: number | null;
  rack?: string | null;
  shelf_id?: number | null;
  shelf?: string | null;
  bin?: string | null;
  boutique_id?: number;
  usage_count: number;
};

type LocationPickerModalProps = {
  subtitle?: string;
  onLocationSelect: (location: string, binId: number) => void;
};

type ContentProps = {
  subtitle?: string;
  onLocationSelect: (location: string, binId: number) => void;
  onClose: () => void;
};

const extractNames = (data: unknown): string[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    if (typeof item === 'string') {
      return item;
    }

    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;

      return String(obj.name || obj.section || obj.rack || obj.shelf || obj.bin || item);
    }

    return String(item);
  });
};

const Chip = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-sm font-medium font-inter transition-colors ${
      selected
        ? 'bg-black text-white'
        : 'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

const LocationPickerContent = ({ subtitle, onLocationSelect, onClose }: ContentProps) => {
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sections, setSections] = useState<string[]>([]);
  const [racks, setRacks] = useState<string[]>([]);
  const [shelves, setShelves] = useState<string[]>([]);
  const [bins, setBins] = useState<string[]>([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [rackInput, setRackInput] = useState('');
  const [shelfInput, setShelfInput] = useState('');
  const [binInput, setBinInput] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [newSectionInput, setNewSectionInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const newSectionInputRef = useRef<HTMLInputElement>(null);

  const fetchSavedLocations = useCallback(async (query?: string) => {
    try {
      const url = query ? `readymade/bins?query=${encodeURIComponent(query)}` : 'readymade/bins';
      const response = await api.getRequest(url);

      if (response.status) {
        setSavedLocations(response.data || []);
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'fetch-locations-error');
      }
    }
  }, []);

  const fetchSections = useCallback(async () => {
    try {
      const response = await api.getRequest('readymade/locations/sections');

      if (response.status) {
        setSections(extractNames(response.data));
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'fetch-sections-error');
      }
    }
  }, []);

  const fetchRacks = useCallback(async (section: string) => {
    try {
      const response = await api.getRequest(
        `readymade/locations/racks?section=${encodeURIComponent(section)}`
      );

      if (response.status) {
        setRacks(extractNames(response.data));
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'fetch-racks-error');
      }
    }
  }, []);

  const fetchShelves = useCallback(async (section: string, rack: string) => {
    try {
      const response = await api.getRequest(
        `readymade/locations/shelves?section=${encodeURIComponent(
          section
        )}&rack=${encodeURIComponent(rack)}`
      );

      if (response.status) {
        setShelves(extractNames(response.data));
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'fetch-shelves-error');
      }
    }
  }, []);

  const fetchBins = useCallback(async (section: string, rack: string, shelf: string) => {
    try {
      const response = await api.getRequest(
        `readymade/locations/bins?section=${encodeURIComponent(section)}&rack=${encodeURIComponent(
          rack
        )}&shelf=${encodeURIComponent(shelf)}`
      );

      if (response.status) {
        setBins(extractNames(response.data));
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'fetch-bins-error');
      }
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSavedLocations(searchQuery || undefined);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, fetchSavedLocations]);

  useEffect(() => {
    if (isAddingSection) {
      newSectionInputRef.current?.focus();
    }
  }, [isAddingSection]);

  const handleSectionClick = useCallback(
    (section: string) => {
      setSelectedSection(section);
      setRackInput('');
      setShelfInput('');
      setBinInput('');
      setRacks([]);
      setShelves([]);
      setBins([]);
      fetchRacks(section);
    },
    [fetchRacks]
  );

  const handleRackChipClick = useCallback(
    (rack: string) => {
      setRackInput(rack);
      setShelfInput('');
      setBinInput('');
      setShelves([]);
      setBins([]);

      if (selectedSection) {
        fetchShelves(selectedSection, rack);
      }
    },
    [selectedSection, fetchShelves]
  );

  const handleShelfChipClick = useCallback(
    (shelf: string) => {
      setShelfInput(shelf);
      setBinInput('');
      setBins([]);

      if (selectedSection && rackInput.trim()) {
        fetchBins(selectedSection, rackInput.trim(), shelf);
      }
    },
    [selectedSection, rackInput, fetchBins]
  );

  const handleBinChipClick = useCallback((bin: string) => {
    setBinInput(bin);
  }, []);

  const handleRackInputChange = useCallback((value: string) => {
    setRackInput(value);
    setShelfInput('');
    setBinInput('');
    setShelves([]);
    setBins([]);
  }, []);

  const handleShelfInputChange = useCallback((value: string) => {
    setShelfInput(value);
    setBinInput('');
    setBins([]);
  }, []);

  const handleSavedLocationClick = useCallback(
    (location: SavedLocation) => {
      onLocationSelect(location.path, location.id);
      onClose();
    },
    [onLocationSelect, onClose]
  );

  const handleAddSection = useCallback(async () => {
    const trimmed = newSectionInput.trim();

    if (!trimmed) {
      return;
    }

    try {
      const response = await api.postRequest('readymade/bins', {
        section: trimmed,
      });

      if (response.status) {
        setNewSectionInput('');
        setIsAddingSection(false);
        fetchSections();
        handleSectionClick(trimmed);
      } else {
        toasts('error', response.message || 'Failed to add section', 'add-section-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'add-section-error');
      }
    }
  }, [newSectionInput, fetchSections, handleSectionClick]);

  const handleSaveLocation = useCallback(async () => {
    if (!selectedSection) {
      toasts('error', 'Please select a section', 'location-section-required');

      return;
    }

    const payload: Record<string, string> = {
      section: selectedSection,
    };
    const rackVal = rackInput.trim();
    const shelfVal = shelfInput.trim();
    const binVal = binInput.trim();

    if (rackVal) {
      payload.rack = rackVal;
    }

    if (shelfVal) {
      payload.shelf = shelfVal;
    }

    if (binVal) {
      payload.bin = binVal;
    }

    try {
      setIsSaving(true);
      const response = await api.postRequest('readymade/bins', payload);

      if (response.status) {
        const created = response.data;
        const locationString =
          created?.path || [selectedSection, rackVal, shelfVal, binVal].filter(Boolean).join(' • ');
        const binId = created?.id;
        onLocationSelect(locationString, binId);
        onClose();
      } else {
        toasts('error', response.message || 'Failed to save location', 'save-location-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'save-location-error');
      }
    } finally {
      setIsSaving(false);
    }
  }, [selectedSection, rackInput, shelfInput, binInput, onLocationSelect, onClose]);

  const preview = useMemo(() => {
    const parts: string[] = [];

    if (selectedSection) {
      parts.push(selectedSection);
    }

    if (rackInput.trim()) {
      parts.push(rackInput.trim());
    }

    if (shelfInput.trim()) {
      parts.push(shelfInput.trim());
    }

    if (binInput.trim()) {
      parts.push(binInput.trim());
    }

    return parts.join(' • ');
  }, [selectedSection, rackInput, shelfInput, binInput]);

  return (
    <div className="flex flex-col" style={{ minHeight: '620px' }}>
      <div className="flex items-start justify-between px-6 pt-5 pb-4">
        <div>
          <h2 className="text-xl font-bold font-inter text-gray-900">Select Location</h2>
          {subtitle && <p className="text-sm text-gray-500 font-inter mt-0.5">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <IoCloseOutline size={24} className="text-gray-500" />
        </button>
      </div>

      <hr className="border-gray-200" />

      <div className="flex flex-1 overflow-hidden">
        <div className="w-[340px] border-r border-gray-200 p-5 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold font-inter text-gray-900">Saved Locations</h3>
            <span className="text-sm text-gray-500 font-inter">
              {savedLocations.length} results
            </span>
          </div>

          <div className="relative mb-3">
            <IoSearchOutline
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm font-inter outline-none focus:border-gray-400 transition-colors"
              placeholder="Search saved locations"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {savedLocations.map((loc) => (
              <button
                key={loc.id}
                type="button"
                onClick={() => handleSavedLocationClick(loc)}
                className="w-full text-left border border-gray-200 rounded-lg p-3 hover:border-gray-400 transition-colors"
              >
                <p className="text-sm font-semibold font-inter text-gray-900">{loc.path}</p>
                {loc.usage_count > 0 && (
                  <p className="text-xs text-gray-500 font-inter mt-1">
                    Used in {loc.usage_count} variants
                  </p>
                )}
              </button>
            ))}

            {savedLocations.length === 0 && (
              <p className="text-sm text-gray-400 font-inter text-center py-8">
                No saved locations found
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 p-5 overflow-y-auto">
          <fieldset className="border border-gray-200 rounded-xl px-4 pt-2 pb-4">
            <legend className="text-sm font-medium font-inter text-gray-700 px-1">Sections</legend>
            <div className="flex flex-wrap gap-2 mt-1">
              {sections.map((section) => (
                <Chip
                  key={section}
                  label={section}
                  selected={selectedSection === section}
                  onClick={() => handleSectionClick(section)}
                />
              ))}
              {isAddingSection ? (
                <input
                  ref={newSectionInputRef}
                  type="text"
                  value={newSectionInput}
                  onChange={(e) => setNewSectionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddSection();
                    }

                    if (e.key === 'Escape') {
                      setIsAddingSection(false);
                      setNewSectionInput('');
                    }
                  }}
                  onBlur={() => {
                    if (!newSectionInput.trim()) {
                      setIsAddingSection(false);
                    }
                  }}
                  className="px-4 py-1.5 border border-gray-300 rounded-full text-sm font-inter outline-none focus:border-gray-500 w-36"
                  placeholder="Section name"
                />
              ) : (
                <Chip
                  label="Add section"
                  selected={false}
                  onClick={() => setIsAddingSection(true)}
                />
              )}
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-4 mt-5">
            <div>
              <p className="text-sm font-medium font-inter text-gray-700 mb-1.5">Rack</p>
              <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white">
                <input
                  type="text"
                  value={rackInput}
                  onChange={(e) => handleRackInputChange(e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800 disabled:bg-white disabled:cursor-not-allowed"
                  placeholder="Rack name"
                  disabled={!selectedSection}
                />
              </div>
              <p className="text-xs text-gray-500 font-inter mt-1.5">
                Select or create within this section
              </p>
              {racks.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {racks.map((rack) => (
                    <Chip
                      key={rack}
                      label={rack}
                      selected={rackInput === rack}
                      onClick={() => handleRackChipClick(rack)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-medium font-inter text-gray-700 mb-1.5">Shelf</p>
              <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white">
                <input
                  type="text"
                  value={shelfInput}
                  onChange={(e) => handleShelfInputChange(e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800 disabled:bg-white disabled:cursor-not-allowed"
                  placeholder="Shelf name"
                  disabled={!rackInput.trim()}
                />
              </div>
              <p className="text-xs text-gray-500 font-inter mt-1.5">
                Select or create within this rack
              </p>
              {shelves.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {shelves.map((shelf) => (
                    <Chip
                      key={shelf}
                      label={shelf}
                      selected={shelfInput === shelf}
                      onClick={() => handleShelfChipClick(shelf)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5">
            <p className="text-sm font-medium font-inter text-gray-700 mb-1.5">Bin (optional)</p>
            <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white">
              <input
                type="text"
                value={binInput}
                onChange={(e) => setBinInput(e.target.value)}
                className="w-full outline-none text-sm font-inter text-gray-800 disabled:bg-white disabled:cursor-not-allowed"
                placeholder="Bin name"
                disabled={!shelfInput.trim()}
              />
            </div>
            <p className="text-xs text-gray-500 font-inter mt-1.5">
              Optional, useful for precise placement
            </p>
            {bins.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {bins.map((bin) => (
                  <Chip
                    key={bin}
                    label={bin}
                    selected={binInput === bin}
                    onClick={() => handleBinChipClick(bin)}
                  />
                ))}
              </div>
            )}
          </div>

          {preview && (
            <div className="mt-5 border border-dashed border-gray-300 rounded-xl px-4 py-3 bg-gray-50">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-inter font-semibold mb-1">
                Preview
              </p>
              <p className="text-sm font-semibold font-inter text-gray-900">{preview}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold font-inter text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveLocation}
          disabled={!selectedSection || isSaving}
          className="px-5 py-2.5 bg-black text-white rounded-lg text-sm font-semibold font-inter hover:bg-gray-800 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Location'}
        </button>
      </div>
    </div>
  );
};

const LocationPickerModal = forwardRef<ModalMethods, LocationPickerModalProps>(
  ({ subtitle, onLocationSelect }, ref) => {
    const modalRef = useRef<ModalMethods>(null);

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      toggle: () => modalRef.current?.toggle(),
      getModalState: () => modalRef.current?.getModalState() ?? false,
    }));

    const handleClose = useCallback(() => {
      modalRef.current?.hide();
    }, []);

    return (
      <Modal ref={modalRef} size="xl" showFooter={false} showCloseIcon={false}>
        <LocationPickerContent
          subtitle={subtitle}
          onLocationSelect={onLocationSelect}
          onClose={handleClose}
        />
      </Modal>
    );
  }
);

LocationPickerModal.displayName = 'LocationPickerModal';

export default LocationPickerModal;
